import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, Home, Flame, PhoneOff, AlarmClock, CalendarClock, Building, FileSignature,
  UserPlus, Plus, StickyNote, Phone, MessageSquare, Mail, Target, TrendingUp, DollarSign, Activity,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import api from "../lib/api";
import { PageHeader, StatCard, Loading, SectionCard, Stamp } from "../components/kit";
import TeamActivity from "../components/TeamActivity";
import QuoteOfTheDay from "../components/QuoteOfTheDay";
import ScoreDigest from "../components/ScoreDigest";
import { money, STAGE_COLORS, titleCase } from "../lib/constants";
import ContactForm from "../components/ContactForm";
import PropertyForm from "../components/PropertyForm";
import { TaskModal, NoteModal, CallModal, SmsModal, EmailModal, OpportunityModal } from "../components/actions";

const QUICK = [
  { key: "contact", label: "New Contact", icon: UserPlus },
  { key: "property", label: "New Property", icon: Home },
  { key: "task", label: "New Task", icon: Plus },
  { key: "note", label: "Add Note", icon: StickyNote },
  { key: "call", label: "Log Call", icon: Phone },
  { key: "sms", label: "Send SMS", icon: MessageSquare },
  { key: "email", label: "Send Email", icon: Mail },
  { key: "opportunity", label: "New Opportunity", icon: Target },
];

const GOLD = ["#D4AF37", "#1E3A8A", "#0EA5E9", "#10B981", "#8B5CF6", "#EC4899", "#F97316", "#64748B"];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [modal, setModal] = useState(null);
  const nav = useNavigate();

  const load = useCallback(() => { api.get("/dashboard/stats").then(({ data }) => setStats(data)); }, []);
  useEffect(() => { load(); }, [load]);

  if (!stats) return <Loading label="Loading dashboard" />;
  const c = stats.counts;

  const pipelineData = Object.entries(stats.pipeline).map(([name, value]) => ({ name, value }));
  const sourceData = Object.entries(stats.leads_by_source).filter(([k]) => k !== "Unknown").map(([name, value]) => ({ name, value })).slice(0, 6);
  const tempData = Object.entries(stats.leads_by_temperature).map(([name, value]) => ({ name: titleCase(name), value }));

  const closeModal = () => { setModal(null); load(); };

  return (
    <div className="animate-fade-up">
      <PageHeader
        title="Command Center"
        subtitle="Your real-estate business at a glance — leads, pipeline, and today's priorities."
        testid="dashboard-header"
        actions={<QuoteOfTheDay />}
      />

      {/* Quick actions */}
      <div className="flex flex-wrap gap-2 mb-8" data-testid="quick-actions">
        {QUICK.map((q) => (
          <button key={q.key} onClick={() => setModal(q.key)} data-testid={`quick-${q.key}`}
            className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-white border border-slate-200 text-sm font-medium text-navy-800 hover:border-gold hover:shadow-sm transition-[border-color,box-shadow]">
            <q.icon className="w-4 h-4 text-gold-dark" />{q.label}
          </button>
        ))}
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <StatCard label="Total Contacts" value={c.total_contacts} icon={Users} accent="navy" onClick={() => nav("/contacts")} testid="stat-contacts" />
        <StatCard label="Active Listings" value={c.active_listings} icon={Building} accent="gold" onClick={() => nav("/properties")} testid="stat-listings" />
        <StatCard label="Hot Leads" value={c.hot_leads} icon={Flame} accent="red" onClick={() => nav("/leads")} testid="stat-hot" />
        <StatCard label="Due Today" value={c.due_today} icon={CalendarClock} accent="blue" onClick={() => nav("/tasks")} testid="stat-due-today" />
        <StatCard label="Overdue" value={c.overdue} icon={AlarmClock} accent="red" onClick={() => nav("/tasks")} testid="stat-overdue" />
        <StatCard label="Pipeline Value" value={money(c.pipeline_value)} icon={DollarSign} accent="green" onClick={() => nav("/pipeline")} testid="stat-pipeline" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pipeline */}
        <SectionCard title="Sales Pipeline" className="lg:col-span-2" testid="dashboard-pipeline"
          action={<button onClick={() => nav("/pipeline")} className="text-xs font-semibold text-gold-dark hover:underline">View board →</button>}>
          {pipelineData.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">No opportunities yet.</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={pipelineData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} />
                <Tooltip cursor={{ fill: "rgba(212,175,55,0.08)" }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {pipelineData.map((e, i) => <Cell key={i} fill={STAGE_COLORS[e.name] || "#1E3A8A"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </SectionCard>

        {/* Today's activity */}
        <SectionCard title="Today's Priorities" testid="dashboard-today">
          <div className="space-y-3">
            {[
              { label: "Follow-ups due today", value: c.due_today, icon: CalendarClock, color: "text-sky-600 bg-sky-50" },
              { label: "Overdue follow-ups", value: c.overdue, icon: AlarmClock, color: "text-red-600 bg-red-50" },
              { label: "New leads this week", value: c.new_leads, icon: UserPlus, color: "text-emerald-600 bg-emerald-50" },
              { label: "Not yet contacted", value: c.not_contacted, icon: PhoneOff, color: "text-amber-600 bg-amber-50" },
              { label: "Active transactions", value: c.active_transactions, icon: FileSignature, color: "text-violet-600 bg-violet-50" },
            ].map((r) => (
              <div key={r.label} className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${r.color}`}><r.icon className="w-4 h-4" /></div>
                <span className="flex-1 text-sm text-slate-600">{r.label}</span>
                <span className="font-display font-bold text-navy-800">{r.value}</span>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <ScoreDigest />
        <SectionCard title="Leads by Source" testid="dashboard-sources">
          {sourceData.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">No source data.</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={sourceData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={45} paddingAngle={2}>
                  {sourceData.map((e, i) => <Cell key={i} fill={GOLD[i % GOLD.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="flex flex-wrap gap-2 mt-2">
            {sourceData.map((s, i) => <span key={s.name} className="flex items-center gap-1.5 text-xs text-slate-500"><span className="w-2.5 h-2.5 rounded-full" style={{ background: GOLD[i % GOLD.length] }} />{s.name} ({s.value})</span>)}
          </div>
        </SectionCard>

        <SectionCard title="This Week's Activity" testid="dashboard-performance">
          <div className="grid grid-cols-3 gap-3 mb-4">
            {[{ l: "Calls", v: stats.performance.calls, i: Phone }, { l: "SMS", v: stats.performance.sms, i: MessageSquare }, { l: "Emails", v: stats.performance.emails, i: Mail }].map((p) => (
              <div key={p.l} className="text-center rounded-lg bg-slate-50 border border-slate-100 py-4">
                <p.i className="w-5 h-5 mx-auto text-gold-dark mb-1.5" />
                <p className="font-display text-2xl font-bold text-navy-800">{p.v}</p>
                <p className="text-xs text-slate-500">{p.l}</p>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            {tempData.map((t) => (
              <div key={t.name} className="flex items-center gap-2">
                <span className="text-xs text-slate-500 w-20">{t.name}</span>
                <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-gold to-gold-dark rounded-full" style={{ width: `${Math.min(100, (t.value / Math.max(...tempData.map((x) => x.value))) * 100)}%` }} />
                </div>
                <span className="text-xs font-semibold text-navy-800 w-6 text-right">{t.value}</span>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Recent Activity" testid="dashboard-activity">
          <div className="space-y-3 max-h-[280px] overflow-y-auto">
            {stats.recent_activity.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No activity yet.</p>}
            {stats.recent_activity.map((a) => (
              <div key={a.id} className="flex gap-3">
                <div className="w-8 h-8 rounded-lg bg-navy-800/5 flex items-center justify-center shrink-0"><Activity className="w-3.5 h-3.5 text-navy-700" /></div>
                <div className="min-w-0">
                  <p className="text-sm text-navy-800 truncate">{a.title}</p>
                  <Stamp by={a.author || "System"} at={a.timestamp} testid={`dash-activity-stamp-${a.id}`} />
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      <div className="mt-6"><TeamActivity /></div>

      <ContactForm open={modal === "contact"} onClose={closeModal} onSaved={load} />
      <PropertyForm open={modal === "property"} onClose={closeModal} onSaved={load} />
      <TaskModal open={modal === "task"} onClose={closeModal} onSaved={load} />
      <NoteModal open={modal === "note"} onClose={closeModal} onSaved={load} />
      <CallModal open={modal === "call"} onClose={closeModal} onSaved={load} />
      <SmsModal open={modal === "sms"} onClose={closeModal} onSaved={load} />
      <EmailModal open={modal === "email"} onClose={closeModal} onSaved={load} />
      <OpportunityModal open={modal === "opportunity"} onClose={closeModal} onSaved={load} />
    </div>
  );
}
